<script setup lang="ts">
import { computed } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import BaseTextInput from '@/components/ui/BaseTextInput.vue';
import BaseDropdown from '@/components/ui/BaseDropdown.vue';
import BaseDateInput from '@/components/ui/BaseDateInput.vue';
import BasePhoneInput from '@/components/ui/BasePhoneInput.vue';
import type { DropdownOption } from '@/components/ui/BaseDropdown.vue';
import { NATIONALITY_OPTIONS } from '@/constants/checkout/nationalities';
import { COUNTRY_OPTIONS } from '@/constants/checkout/countries';
import { $t } from '@/composables/translations/useTranslate';
import {
  ContactPersonBaseSchema,
  ContactPersonTravelingSchema,
  type ContactPerson,
} from '@/types/schemas/checkout/contact';

/**
 * Checkout Contact Form Component
 *
 * Renders the contact person (invoice recipient) form with conditional fields.
 * Owns its own useForm() instance and exposes validation methods via defineExpose().
 *
 * When isTraveling = true (solo mode):
 *   Shows 10 fields including salutation and birthDate (18+).
 *   Contact person data is used as first traveler in DaVinci Participants.
 *
 * When isTraveling = false (accompany mode):
 *   Shows 8 fields (excludes salutation and birthDate).
 *   Contact person data is used only for DaVinci Client.
 *
 * Validation error messages from Zod schemas are translation keys
 * that are passed directly to $t() for translation.
 */

interface Props {
  initialValues?: Partial<ContactPerson>;
  isTraveling: boolean;
}

const props = defineProps<Props>();

// Default Values
function getDefaultContactValues(): Partial<ContactPerson> {
  return {
    salutation: undefined,
    birthDate: '',
    firstName: '',
    lastName: '',
    street: '',
    postalCode: '',
    location: '',
    country: undefined,
    nationality: undefined,
    mobile: '',
    email: '',
  };
}

const dynamicValidationSchema = computed(() =>
  toTypedSchema(props.isTraveling ? ContactPersonTravelingSchema : ContactPersonBaseSchema)
);

const { defineField, errors, validate, values, resetForm, meta, setFieldValue } =
  useForm<ContactPerson>({
    validationSchema: dynamicValidationSchema,
    initialValues: props.initialValues ?? getDefaultContactValues(),
  });

// Field Definitions
const [salutationValue] = defineField('salutation');
const [birthDateValue] = defineField('birthDate');
const [firstNameValue] = defineField('firstName');
const [lastNameValue] = defineField('lastName');
const [streetValue] = defineField('street');
const [postalCodeValue] = defineField('postalCode');
const [locationValue] = defineField('location');
const [countryValue] = defineField('country');
const [nationalityValue] = defineField('nationality');
const [mobileValue] = defineField('mobile');
const [emailValue] = defineField('email');

/**
 * Computed property that translates all validation error messages.
 * Error messages from Zod schemas are translation keys (e.g., 'ibe.validation.firstName.required')
 * that are passed directly to $t() for translation.
 */
const translatedErrors = computed(() => {
  const result: Record<string, string> = {};
  for (const [field, message] of Object.entries(errors.value)) {
    result[field] = message ? $t(message) : '';
  }
  return result;
});

// Exposed Methods (for parent component via ref)
defineExpose({
  async validate() {
    const result = await validate();
    return {
      valid: result.valid,
      errors: { ...errors.value } as Record<string, string>,
    };
  },
  getValues(): ContactPerson {
    return { ...values } as ContactPerson;
  },
  getErrors(): Record<string, string | undefined> {
    return { ...errors.value };
  },
  reset() {
    resetForm();
  },
  isDirty(): boolean {
    return meta.value.dirty;
  },
  setFieldValue(field: string, value: unknown) {
    setFieldValue(field as keyof ContactPerson, value as ContactPerson[keyof ContactPerson]);
  },
});

const salutationOptions = computed<DropdownOption[]>(() => [
  { value: 'mr', label: $t('ibe.form.salutation.mr', 'Mr') },
  { value: 'mrs', label: $t('ibe.form.salutation.mrs', 'Mrs') },
  { value: 'diverse', label: $t('ibe.form.salutation.diverse', 'Diverse') },
]);
</script>

<template>
  <div class="checkout-contact-form mb-12">
    <h3 class="h3-styling mb-6 text-text-primary">
      {{ $t('ibe.checkout.contact.title', 'Contact Person') }}
    </h3>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
      <!-- Salutation (only when traveling) -->
      <BaseDropdown
        v-if="isTraveling"
        id="contact-salutation"
        v-model="salutationValue"
        name="salutation"
        :label="$t('ibe.form.label.salutation', 'Salutation *')"
        :placeholder="$t('ibe.form.placeholder.salutation', 'Select salutation')"
        :options="salutationOptions"
        :has-error="!!errors.salutation"
        :error-message="translatedErrors.salutation"
      />

      <!-- Birth Date (only when traveling) -->
      <BaseDateInput
        v-if="isTraveling"
        id="contact-birthDate"
        v-model="birthDateValue"
        name="birthDate"
        :label="$t('ibe.form.label.birthDate', 'Birth date *')"
        :placeholder="$t('ibe.form.placeholder.birthDate', 'DD.MM.YYYY (e.g. 25.12.1980)')"
        :error="!!errors.birthDate"
        :error-message="translatedErrors.birthDate"
      />

      <!-- First Name -->
      <BaseTextInput
        id="contact-firstName"
        v-model="firstNameValue"
        type="text"
        name="firstName"
        :label="$t('ibe.form.label.firstName', 'First name *')"
        :placeholder="$t('ibe.form.placeholder.firstName', 'Full first name(s) as per passport')"
        :error="!!errors.firstName"
        :error-message="translatedErrors.firstName"
      />

      <!-- Last Name -->
      <BaseTextInput
        id="contact-lastName"
        v-model="lastNameValue"
        type="text"
        name="lastName"
        :label="$t('ibe.form.label.lastName', 'Last name *')"
        :placeholder="$t('ibe.form.placeholder.lastName', 'Last name as per passport')"
        :error="!!errors.lastName"
        :error-message="translatedErrors.lastName"
      />

      <!-- Street -->
      <BaseTextInput
        id="contact-street"
        v-model="streetValue"
        type="text"
        name="street"
        :label="$t('ibe.form.label.street', 'Street and house number *')"
        :placeholder="$t('ibe.form.placeholder.street', 'e.g. Sample Street 12')"
        :error="!!errors.street"
        :error-message="translatedErrors.street"
      />

      <!-- Postal Code -->
      <BaseTextInput
        id="contact-postalCode"
        v-model="postalCodeValue"
        type="text"
        name="postalCode"
        :label="$t('ibe.form.label.postalCode', 'Postal code *')"
        :placeholder="$t('ibe.form.placeholder.postalCode', 'e.g. 10115')"
        :error="!!errors.postalCode"
        :error-message="translatedErrors.postalCode"
      />

      <!-- Location (City) -->
      <BaseTextInput
        id="contact-location"
        v-model="locationValue"
        type="text"
        name="location"
        :label="$t('ibe.form.label.location', 'City *')"
        :placeholder="$t('ibe.form.placeholder.location', 'e.g. Berlin')"
        :error="!!errors.location"
        :error-message="translatedErrors.location"
      />

      <!-- Country -->
      <BaseDropdown
        id="contact-country"
        v-model="countryValue"
        name="country"
        :label="$t('ibe.form.label.country', 'Country *')"
        :placeholder="$t('ibe.form.placeholder.country', 'Select country')"
        :options="COUNTRY_OPTIONS"
        :has-error="!!errors.country"
        :error-message="translatedErrors.country"
      />

      <!-- Nationality (only when traveling — contact person is also a traveler) -->
      <BaseDropdown
        v-if="isTraveling"
        id="contact-nationality"
        v-model="nationalityValue"
        name="nationality"
        :label="$t('ibe.form.label.nationality', 'Nationality *')"
        :placeholder="$t('ibe.form.placeholder.nationality', 'Select nationality')"
        :options="NATIONALITY_OPTIONS"
        :has-error="!!errors.nationality"
        :error-message="translatedErrors.nationality"
      />

      <!-- Mobile Phone -->
      <BasePhoneInput
        id="contact-mobile"
        v-model="mobileValue"
        name="mobile"
        :label="$t('ibe.form.label.mobile', 'Mobile phone *')"
        :placeholder="$t('ibe.form.placeholder.mobile', 'e.g. 160 1234567')"
        :error="!!errors.mobile"
        :error-message="translatedErrors.mobile"
      />

      <!-- Email -->
      <BaseTextInput
        id="contact-email"
        v-model="emailValue"
        type="email"
        name="email"
        :label="$t('ibe.form.label.email', 'Email address *')"
        :placeholder="$t('ibe.form.placeholder.email', 'e.g. name@email.de')"
        :error="!!errors.email"
        :error-message="translatedErrors.email"
      />
    </div>
  </div>
</template>
